<script setup>
/**
 * Fixed bottom bar under 900px.
 *
 * Slides up once the hero has scrolled away, hides again over the lead form
 * (no point covering the thing it points at) and while a field has focus, so
 * it never sits on top of the on-screen keyboard.
 */
import { ref, onMounted, onUnmounted } from "vue";
import BaseButton from "../ui/BaseButton.vue";
import { scrollToLeadForm } from "../../composables/useScrollTo";

const visible = ref(false);
const typing = ref(false);

let observers = [];

function watchSection(id, onChange) {
  const el = document.getElementById(id);
  if (!el) return;
  const observer = new IntersectionObserver(
    ([entry]) => onChange(entry.isIntersecting),
    { threshold: 0 }
  );
  observer.observe(el);
  observers.push(observer);
}

let heroVisible = true;
let formVisible = false;

function update() {
  visible.value = !heroVisible && !formVisible;
}

function onFocusIn(event) {
  typing.value = ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName);
}

function onFocusOut() {
  typing.value = false;
}

onMounted(() => {
  watchSection("hero", (isIn) => {
    heroVisible = isIn;
    update();
  });
  watchSection("lead-form", (isIn) => {
    formVisible = isIn;
    update();
  });

  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);
});

onUnmounted(() => {
  observers.forEach((o) => o.disconnect());
  observers = [];
  document.removeEventListener("focusin", onFocusIn);
  document.removeEventListener("focusout", onFocusOut);
});
</script>

<template>
  <Transition name="bar">
    <div v-if="visible && !typing" class="cta-bar">
      <div class="cta-bar__text">
        <strong>רוצים לראות איך זה עובד?</strong>
        <span>הדגמה קצרה של המערכת</span>
      </div>
      <BaseButton size="md" @click="scrollToLeadForm('mobile_bar')">
        צרו קשר
      </BaseButton>
    </div>
  </Transition>
</template>

<style scoped>
.cta-bar {
  position: fixed;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: var(--z-sticky);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-3) var(--gutter);
  padding-block-end: max(var(--space-3), env(safe-area-inset-bottom));
  background: var(--glass-solid);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-block-start: var(--border);
  box-shadow: 0 -4px 20px rgba(10, 37, 64, 0.08);
}

.cta-bar__text {
  display: flex;
  flex-direction: column;
  min-inline-size: 0;
}

.cta-bar__text strong {
  font-size: var(--text-sm);
  color: var(--ink);
}

.cta-bar__text span {
  font-size: var(--text-xs);
  color: var(--ink-subtle);
}

.bar-enter-active,
.bar-leave-active {
  transition: transform var(--dur-base) var(--ease-out),
    opacity var(--dur-base) var(--ease-out);
}

.bar-enter-from,
.bar-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

@media (min-width: 900px) {
  .cta-bar {
    display: none;
  }
}
</style>
